import { isUndefined } from 'lodash/fp';
import React, { UIEvent, useCallback, useEffect, useRef } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';

import styles from './Scrollable.module.scss';

export interface ScrollableElement {
    scrollTop: number;
    totalHeight: number;
    scrollableHeight: number;
}

export interface ScrollableProps {
    children: JSX.Element | null | (JSX.Element | JSX.Element[] | false | null)[];
    height?: string;
    width?: string;
    maxHeight?: string;
    autoHeight?: boolean;
    handleScroll?: (scrollableElement: ScrollableElement) => void;
    autoCenterOnMount?: boolean;
    paddingBottom?: string;
}

export const Scrollable: React.FC<ScrollableProps> = ({ children, height = '100%', maxHeight = '100%', width = '100%', autoHeight = true, handleScroll, autoCenterOnMount, paddingBottom = '4px' }) => {
    const onScroll = useCallback((e: UIEvent) => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const scrollElement = (e as any).srcElement as HTMLDivElement;
        const { scrollTop, clientHeight, scrollHeight } = scrollElement;
        if (!isUndefined(handleScroll)) {
            handleScroll({ scrollTop, totalHeight: scrollHeight, scrollableHeight: clientHeight });
        }
    }, [handleScroll]);

    const scrollRef = useRef<Scrollbars>(null);

    useEffect(() => {
        const scrollable = scrollRef.current;
        if (autoCenterOnMount && scrollable) {
            const scrollWidth = scrollable.getScrollWidth();
            const clientWidth = scrollable.getClientWidth();
            const scrollLeft = (scrollWidth - clientWidth) / 2;

            const scrollHeight = scrollable.getScrollHeight();
            const clientHeight = scrollable.getClientHeight();
            const scrollTop = (scrollHeight - clientHeight) / 2;

            scrollable.scrollLeft(scrollLeft);
            scrollable.scrollTop(scrollTop);
        }
    }, [autoCenterOnMount]);

    return (
        <Scrollbars
            style={{ width, height, paddingBottom }}
            autoHeight={autoHeight}
            autoHeightMin={height}
            autoHeightMax={maxHeight}
            hideTracksWhenNotNeeded
            onScroll={onScroll}
            ref={scrollRef}
            renderThumbVertical={props => <div {...props} className={styles.thumb} />}
            renderThumbHorizontal={props => <div {...props} className={styles.thumb} />}
        >
            {children}
        </Scrollbars>
    );
};
